import { Box, Typography } from '@mui/material';
import type { NotificationItem as NotificationItemType } from '@/store/api/notificationsApi';

const SEVERITY_COLORS: Record<NotificationItemType['severity'], string> = {
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${String(diffMin)}m ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${String(diffHours)}h ago`;

  return new Date(isoString).toLocaleDateString();
}

interface Props {
  notification: NotificationItemType;
  onClick: (n: NotificationItemType) => void;
}

export function NotificationItem({ notification, onClick }: Props) {
  const borderColor = SEVERITY_COLORS[notification.severity];

  return (
    <Box
      data-testid="notification-item"
      onClick={() => {
        onClick(notification);
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: borderColor,
        bgcolor: notification.is_read ? 'transparent' : 'rgba(0,0,0,0.04)',
        opacity: notification.is_read ? 0.6 : 1,
        '&:hover': {
          bgcolor: 'var(--color-surface-glass-hover, rgba(0,0,0,0.06))',
        },
      }}
    >
      {/* Unread dot */}
      <Box sx={{ pt: 0.5, minWidth: 8 }}>
        {!notification.is_read && (
          <Box
            data-testid="unread-dot"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#3b82f6',
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: notification.is_read ? 400 : 600,
            fontSize: '0.8125rem',
            lineHeight: 1.4,
          }}
          noWrap
        >
          {notification.title}
        </Typography>
        {notification.message !== null && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {notification.message}
          </Typography>
        )}
        <Typography
          data-testid="notification-timestamp"
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}
        >
          {formatRelativeTime(notification.created_at)}
        </Typography>
      </Box>
    </Box>
  );
}
